const SORT_MAP = {
  popular: 'best-selling',
  newest: 'created-descending',
  'price-asc': 'price-ascending',
  'price-desc': 'price-descending',
};

const SORT_VALUE_TO_KEY = {
  'best-selling': 'popular',
  'created-descending': 'newest',
  'price-ascending': 'price-asc',
  'price-descending': 'price-desc',
};

class CollectionFilters extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.baseUrl = this.dataset.url || window.location.pathname;

    this.onSortClick = this.handleSortClick.bind(this);
    this.onFabClick = this.handleFabClick.bind(this);
    this.onCloseClick = this.handleCloseClick.bind(this);
    this.onDialogClick = this.handleDialogClick.bind(this);
    this.onDialogClose = this.handleDialogClose.bind(this);
    this.onFormSubmit = this.handleFormSubmit.bind(this);
    this.onFormReset = this.handleFormReset.bind(this);
    this.onPopoverTriggerClick = this.handlePopoverTriggerClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
  }

  connectedCallback() {
    this.reInitialize(true);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
  }

  disconnectedCallback() {
    this.unbindEvents();
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  }

  reInitialize(isFirstLoad = false) {
    this.sortButtons = Array.from(this.querySelectorAll('.sort-btn'));
    this.priceButton = this.querySelector('.sort-btn--price');
    this.fab = this.querySelector('.collection-filter-fab');
    this.fabIndicator = this.querySelector('.collection-filter-fab__indicator');
    this.filterTriggerIndicator = this.querySelector('.facet-button__indicator');
    this.dialog = this.querySelector('.collection-filter-sheet');
    this.closeButton = this.querySelector('.collection-filter-sheet__close');

    this.forms = Array.from(this.querySelectorAll('.collection-filter-form'));
    this.form = this.forms[0];

    this.sortPopover = this.querySelector('[data-popover="sort"]');
    this.filterPopover = this.querySelector('[data-popover="filter"]');
    this.sortTrigger = this.querySelector('[data-popover-trigger="sort"]');
    this.filterTrigger = this.querySelector('[data-popover-trigger="filter"]');
    this.sortTriggerLabel = this.querySelector('.desktop-sort-trigger__label');

    this.sortLabels = {};
    this.querySelectorAll('.sort-menu-item').forEach((item) => {
      this.sortLabels[item.dataset.sortKey] = item.textContent.trim();
    });

    if (isFirstLoad) {
      this.appliedFilterState = this.snapshotForm(this.form);
    }

    this.syncSortFromUrl();
    this.updateFilterIndicators();
    
    this.bindEvents();
  }

  bindEvents() {
    this.sortButtons.forEach((btn) => btn.addEventListener('click', this.onSortClick));
    if (this.fab) this.fab.addEventListener('click', this.onFabClick);
    if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
    if (this.dialog) {
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('close', this.onDialogClose);
    }
    this.forms.forEach((form) => {
      form.addEventListener('submit', this.onFormSubmit);
      form.addEventListener('reset', this.onFormReset);
    });
    if (this.sortTrigger) this.sortTrigger.addEventListener('click', this.onPopoverTriggerClick);
    if (this.filterTrigger) this.filterTrigger.addEventListener('click', this.onPopoverTriggerClick);
  }

  unbindEvents() {
    if (this.sortButtons) this.sortButtons.forEach((btn) => btn.removeEventListener('click', this.onSortClick));
    if (this.fab) this.fab.removeEventListener('click', this.onFabClick);
    if (this.closeButton) this.closeButton.removeEventListener('click', this.onCloseClick);
    if (this.dialog) {
      this.dialog.removeEventListener('click', this.onDialogClick);
      this.dialog.removeEventListener('close', this.onDialogClose);
    }
    if (this.forms) {
      this.forms.forEach((form) => {
        form.removeEventListener('submit', this.onFormSubmit);
        form.removeEventListener('reset', this.onFormReset);
      });
    }
    if (this.sortTrigger) this.sortTrigger.removeEventListener('click', this.onPopoverTriggerClick);
    if (this.filterTrigger) this.filterTrigger.removeEventListener('click', this.onPopoverTriggerClick);
  }

  syncSortFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const sortValue = params.get('sort_by');
    const key = sortValue ? SORT_VALUE_TO_KEY[sortValue] : 'popular';
    this.setActiveSort(key || 'popular');
  }

  setActiveSort(sortKey) {
    this.sortButtons.forEach((btn) => {
      const key = btn.dataset.sortKey;
      const isActive = key === 'price'
        ? sortKey === 'price-asc' || sortKey === 'price-desc'
        : key === sortKey;
      btn.classList.toggle('is-active', isActive);
    });

    if (this.priceButton) {
      const icons = this.priceButton.querySelectorAll('.sort-btn__icon');
      icons.forEach((icon) => {
        icon.hidden = true;
      });
      let nextIcon;
      if (sortKey === 'price-asc') {
        nextIcon = this.priceButton.querySelector('[data-sort-icon="asc"]');
      } else if (sortKey === 'price-desc') {
        nextIcon = this.priceButton.querySelector('[data-sort-icon="desc"]');
      } else {
        nextIcon = this.priceButton.querySelector('[data-sort-icon="idle"]');
      }
      if (nextIcon) nextIcon.hidden = false;
    }

    if (this.sortTriggerLabel) {
      const label = this.sortLabels[sortKey] || this.sortLabels.popular;
      if (label) this.sortTriggerLabel.textContent = label;
    }

    this.activeSortKey = sortKey;
  }

  handleSortClick(event) {
    const btn = event.currentTarget;
    const key = btn.dataset.sortKey;

    let nextKey;
    if (key === 'price') {
      if (this.activeSortKey === 'price-asc') {
        nextKey = 'price-desc';
      } else if (this.activeSortKey === 'price-desc') {
        nextKey = 'price-asc';
      } else {
        nextKey = 'price-asc';
      }
    } else {
      nextKey = key;
    }

    this.setActiveSort(nextKey);
    this.closeAllPopovers({ restoreFilter: false });
    this.applyChanges();
  }

  handleFabClick() {
    if (!this.dialog) return;
    this.appliedFilterState = this.snapshotForm(this.form);
    this.dialog.showModal();
  }

  handleCloseClick() {
    if (!this.dialog) return;
    this.dialog.close();
  }

  handleDialogClick(event) {
    if (event.target === this.dialog) {
      this.dialog.close();
    }
  }

  handleDialogClose() {
    this.restoreAllForms(this.appliedFilterState);
  }

  handleFormSubmit(event) {
    event.preventDefault();
    const sourceForm = event.currentTarget;
    const snapshot = this.snapshotForm(sourceForm);
    this.forms.forEach((form) => {
      if (form !== sourceForm) this.restoreFormInto(form, snapshot);
    });
    this.appliedFilterState = snapshot;
    this.applyChanges();
    if (this.dialog && this.dialog.open) this.dialog.close();
    this.closeAllPopovers({ restoreFilter: false });
  }

  handleFormReset(event) {
    const form = event.currentTarget;
    requestAnimationFrame(() => {
      form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        input.checked = false;
      });
      form.querySelectorAll('input[type="number"]').forEach((input) => {
        input.value = '';
      });
    });
  }

  handlePopoverTriggerClick(event) {
    const trigger = event.currentTarget;
    const name = trigger.dataset.popoverTrigger;
    this.togglePopover(name);
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) {
      const inSort = this.sortPopover && (this.sortPopover.contains(event.target) || (this.sortTrigger && this.sortTrigger.contains(event.target)));
      const inFilter = this.filterPopover && (this.filterPopover.contains(event.target) || (this.filterTrigger && this.filterTrigger.contains(event.target)));
      if (inSort || inFilter) return;
    }
    this.closeAllPopovers();
  }

  handleDocumentKeydown(event) {
    if (event.key === 'Escape') this.closeAllPopovers();
  }

  togglePopover(name) {
    const popover = name === 'sort' ? this.sortPopover : this.filterPopover;
    const trigger = name === 'sort' ? this.sortTrigger : this.filterTrigger;
    if (!popover || !trigger) return;

    const willOpen = popover.hasAttribute('hidden');
    this.closeAllPopovers();

    if (willOpen) {
      if (name === 'filter') {
        this.appliedFilterState = this.snapshotForm(this.form);
      }
      popover.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
    }
  }

  closeAllPopovers(options = {}) {
    const { restoreFilter = true } = options;
    const filterWasOpen = this.filterPopover && !this.filterPopover.hasAttribute('hidden');

    if (this.sortPopover) this.sortPopover.hidden = true;
    if (this.filterPopover) this.filterPopover.hidden = true;
    if (this.sortTrigger) this.sortTrigger.setAttribute('aria-expanded', 'false');
    if (this.filterTrigger) this.filterTrigger.setAttribute('aria-expanded', 'false');

    if (restoreFilter && filterWasOpen && this.appliedFilterState) {
      const desktopForm = this.filterPopover ? this.filterPopover.querySelector('.collection-filter-form') : null;
      if (desktopForm) this.restoreFormInto(desktopForm, this.appliedFilterState);
    }
  }

  snapshotForm(form) {
    if (!form) return { checkboxes: {}, numbers: {} };
    const snapshot = { checkboxes: {}, numbers: {} };
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      snapshot.checkboxes[`${input.name}|${input.value}`] = input.checked;
    });
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      snapshot.numbers[input.name] = input.value;
    });
    return snapshot;
  }

  restoreFormInto(form, snapshot) {
    if (!form || !snapshot) return;
    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const key = `${input.name}|${input.value}`;
      input.checked = Boolean(snapshot.checkboxes && snapshot.checkboxes[key]);
    });
    form.querySelectorAll('input[type="number"]').forEach((input) => {
      input.value = (snapshot.numbers && snapshot.numbers[input.name]) || '';
    });
  }

  restoreAllForms(snapshot) {
    this.forms.forEach((form) => this.restoreFormInto(form, snapshot));
  }

  buildParams() {
    const params = new URLSearchParams(window.location.search);

    const sortValue = SORT_MAP[this.activeSortKey];
    if (sortValue && sortValue !== 'best-selling') {
      params.set('sort_by', sortValue);
    } else {
      params.delete('sort_by');
    }

    if (this.form) {
      const formData = new FormData(this.form);
      
      // Clear out existing filter params first
      Array.from(params.keys()).forEach(key => {
        if (key.startsWith('filter.')) params.delete(key);
      });

      for (const [name, value] of formData.entries()) {
        if (value === '' || value == null) continue;
        params.append(name, value);
      }
    }

    return params;
  }

  async applyChanges() {
    const params = this.buildParams();
    const queryString = params.toString();
    const nextUrl = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    history.replaceState(null, '', nextUrl);
    this.updateFilterIndicators(params);

    const fetchParams = new URLSearchParams(params);
    fetchParams.set('section_id', this.sectionId);
    
    // Explicitly add partial matching and product-only filter for search queries behind the scenes
    if (fetchParams.has('q') || window.location.pathname.includes('/search')) {
      fetchParams.set('options[prefix]', 'last');
      fetchParams.set('type', 'product');
    }

    const fetchUrl = `${this.baseUrl}?${fetchParams.toString()}`;

    this.classList.add('is-loading');

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const htmlText = await response.text();
      const parser = new DOMParser();
      const newDocument = parser.parseFromString(htmlText, 'text/html');

      // Unbind previous events before wiping DOM
      this.unbindEvents();

      // Update Header block (results count, empty state, etc) if it exists
      if (this.querySelector('#search-results-area') && newDocument.querySelector('#search-results-area')) {
        this.replaceNode('#search-results-area', newDocument);
      }

      // We still want to handle standard collection grids directly if they exist
      if (!this.querySelector('#search-results-area') && this.querySelector('.collection-grid') && newDocument.querySelector('.collection-grid')) {
        this.replaceNode('.collection-grid', newDocument);
      }
      if (!this.querySelector('#search-results-area')) {
        this.replaceFooter(newDocument);
      }

      // Re-initialize DOM queries and events
      this.reInitialize();
    } catch (error) {
      console.error('Error applying filters:', error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  replaceNode(selector, newDocument) {
    const newNode = newDocument.querySelector(selector);
    const currentNode = this.querySelector(selector);
    
    if (newNode && currentNode) {
      // Direct replacement
      currentNode.innerHTML = newNode.innerHTML;
    } else if (newNode && !currentNode) {
      // Append if it doesn't exist but is in the new document
      this.appendChild(newNode);
    } else if (!newNode && currentNode) {
      // Remove if it exists currently but not in the new document
      currentNode.remove();
    }
  }

  replaceFooter(newDocument) {
    const newFooter = newDocument.querySelector('.collection-footer');
    const currentFooter = this.querySelector('.collection-footer');

    if (newFooter && currentFooter) {
      currentFooter.replaceWith(newFooter);
    } else if (newFooter && !currentFooter) {
      const grid = this.querySelector('.collection-grid');
      if (grid && grid.parentNode) {
        grid.parentNode.insertBefore(newFooter, grid.nextSibling);
      }
    } else if (!newFooter && currentFooter) {
      currentFooter.remove();
    }
  }

  updateFilterIndicators(params) {
    const source = params || new URLSearchParams(window.location.search);
    let hasFilter = false;
    for (const key of source.keys()) {
      if (key.startsWith('filter.')) {
        hasFilter = true;
        break;
      }
    }
    if (this.fabIndicator) this.fabIndicator.hidden = !hasFilter;
    if (this.filterTriggerIndicator) this.filterTriggerIndicator.hidden = !hasFilter;
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
